import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
  Avatar,
  alpha,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ColorModeContext } from '../App';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SensorsIcon from '@mui/icons-material/Sensors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import SpeedIcon from '@mui/icons-material/Speed';
import SavingsIcon from '@mui/icons-material/Savings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import RouterIcon from '@mui/icons-material/Router';
import StorageIcon from '@mui/icons-material/Storage';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Import assets
import wteLogo from '../assets/wte_logo.png';
import dashboardScreenshot from '../assets/wte-oms-dashboard.png';
import architectureDiagram from '../assets/system_architecture2.png';
import ClientsShowcase from '../components/ClientsShowcase';

// Grain texture component
const GrainTexture = ({ opacity = 0.1 }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      opacity,
      zIndex: 1,
      pointerEvents: 'none',
    }}
  />
);

// Blur effect component - theme aware
const BlurEffect = ({ color, size = '200px', top, left, right, bottom, theme }) => {
  const isDark = theme?.palette?.mode === 'dark';
  const effectiveColor = color || (isDark ? '#64b5f6' : '#bbdefb');
  
  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${effectiveColor} 0%, transparent 70%)`,
        filter: 'blur(40px)',
        opacity: isDark ? 0.3 : 0.4,
        top,
        left,
        right,
        bottom,
        zIndex: 0,
      }}
    />
  );
};

// Gradient background component - theme aware
const GradientBackground = ({ colors, direction = '135deg', theme }) => {
  const isDark = theme?.palette?.mode === 'dark';
  // Default: subtle darker blues for dark mode, original light blues for light mode
  const defaultColors = colors || (isDark 
    ? ['#1a237e', '#283593', '#3949ab']
    : ['#1976d2', '#0d47a1']
  );
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(${direction}, ${defaultColors.join(' 0%, ')} 100%)`,
        zIndex: 0,
      }}
    />
  );
};

// Global background component - theme aware
const GlobalBackground = ({ theme }) => {
  const isDark = theme?.palette?.mode === 'dark';
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        background: isDark 
          ? 'linear-gradient(135deg, #0f1424 0%, #1a1f36 100%)'
          : 'linear-gradient(135deg, #f5f9ff 0%, #e3f2fd 100%)',
        transition: 'background 0.3s ease',
      }}
    >
      <BlurEffect color={isDark ? "#1e3a8a" : "#bbdefb"} top="5%" left="5%" size="400px" theme={theme} />
      <BlurEffect color={isDark ? "#1e40af" : "#e3f2fd"} top="40%" right="10%" size="350px" theme={theme} />
      <BlurEffect color={isDark ? "#2563eb" : "#90caf9"} bottom="10%" left="20%" size="300px" theme={theme} />
      <BlurEffect color={isDark ? "#1e3a8a" : "#bbdefb"} bottom="30%" right="30%" size="250px" theme={theme} />
      <GrainTexture opacity={isDark ? 0.05 : 0.03} />
    </Box>
  );
};

// Modern feature card with hover effects - theme aware
const FeatureCard = ({ icon, title, description, theme }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isDark = theme?.palette?.mode === 'dark';
  
  return (
    <Card 
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        background: isDark
          ? 'linear-gradient(145deg, rgba(17,24,39,0.9) 0%, rgba(31,41,55,0.7) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-12px)',
          boxShadow: isDark 
            ? '0 20px 40px rgba(0, 0, 0, 0.5)'
            : '0 20px 40px rgba(0, 0, 0, 0.08)',
          borderColor: 'primary.light',
          background: isDark
            ? 'linear-gradient(145deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.8) 100%)'
            : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
        }
      }}
    >
      <Box 
        sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isDark
            ? `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
            : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
          transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.4s ease',
        }}
      />
      <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 3,
            width: '64px',
            height: '64px',
            borderRadius: '20px',
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
              : 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
            color: 'white',
            alignItems: 'center',
            alignSelf: 'flex-start',
            boxShadow: isDark
              ? `0 8px 16px ${alpha(theme.palette.primary.main, 0.4)}`
              : '0 8px 16px rgba(33, 150, 243, 0.3)',
            transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
            transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          }}
        >
          {icon}
        </Box>
        <Typography 
          variant="h6" 
          component="h3" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            fontSize: '1.25rem',
            mb: 2,
            color: isDark ? 'primary.light' : undefined,
            background: isDark
              ? undefined
              : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
            backgroundClip: isDark ? undefined : 'text',
            textFillColor: isDark ? undefined : 'transparent',
            WebkitBackgroundClip: isDark ? undefined : 'text',
            WebkitTextFillColor: isDark ? undefined : 'transparent',
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            lineHeight: 1.7,
            flexGrow: 1
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Modern benefit item with hover effect - theme aware
const BenefitItem = ({ icon, text, theme }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isDark = theme?.palette?.mode === 'dark';
  
  return (
    <ListItem 
      sx={{ 
        py: 2,
        transition: 'all 0.3s ease',
        borderRadius: '12px',
        '&:hover': {
          bgcolor: isDark 
            ? 'rgba(10, 132, 255, 0.1)'
            : 'rgba(33, 150, 243, 0.05)',
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ListItemIcon>
        <Avatar 
          sx={{ 
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
              : 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
            color: 'white',
            width: 48,
            height: 48,
            boxShadow: isDark
              ? `0 8px 16px ${alpha(theme.palette.primary.main, 0.4)}`
              : '0 8px 16px rgba(33, 150, 243, 0.3)',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            transition: 'transform 0.3s ease',
          }}
        >
          {icon}
        </Avatar>
      </ListItemIcon>
      <ListItemText 
        primary={text} 
        primaryTypographyProps={{ 
          fontWeight: 600,
          fontSize: '1.1rem',
          color: isHovered ? 'primary.main' : 'text.primary',
          transition: 'color 0.3s ease',
        }}
      />
    </ListItem>
  );
};

// Modern button component
const ModernButton = ({ variant = 'contained', children, startIcon, endIcon, onClick, sx = {} }) => (
  <Button
    variant={variant}
    size="large"
    startIcon={startIcon}
    endIcon={endIcon}
    onClick={onClick}
    sx={{
      py: 1.5,
      px: 4,
      borderRadius: '12px',
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
      letterSpacing: '0.5px',
      boxShadow: variant === 'contained' ? '0 8px 16px rgba(0, 0, 0, 0.1)' : 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: variant === 'contained' ? '0 12px 20px rgba(0, 0, 0, 0.15)' : 'none',
      },
      ...sx
    }}
  >
    {children}
  </Button>
);

// Architecture flow component - theme aware
const ArchitectureFlow = ({ theme: themeProp }) => {
  const themeFromHook = useTheme();
  const theme = themeProp || themeFromHook;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme?.palette?.mode === 'dark';
  
  const nodes = [
    { 
      icon: <SensorsIcon sx={{ fontSize: 32 }} />, 
      label: 'Instruments & Sensors',
      color: '#4caf50',
      description: 'Water quality sensors, flow meters, and monitoring devices'
    },
    { 
      icon: <DeviceHubIcon sx={{ fontSize: 32 }} />, 
      label: 'PLC',
      color: '#2196f3',
      description: 'Programmable Logic Controller for local data processing'
    },
    { 
      icon: <RouterIcon sx={{ fontSize: 32 }} />, 
      label: 'IoT Gateway',
      color: '#ff9800',
      description: 'Edge device for data collection and transmission'
    },
    { 
      icon: <StorageIcon sx={{ fontSize: 32 }} />, 
      label: 'Cloud Server',
      color: '#9c27b0',
      description: 'Secure cloud infrastructure for data storage and processing'
    },
    { 
      icon: <DashboardIcon sx={{ fontSize: 32 }} />, 
      label: 'WTE OMS',
      color: '#f44336',
      description: 'User interface for monitoring and control'
    }
  ];

  return (
      <Box sx={{ 
        position: 'relative',
        py: 4,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: isDark
            ? 'linear-gradient(180deg, rgba(30, 58, 138, 0.3) 0%, rgba(15, 20, 36, 0) 100%)'
            : 'linear-gradient(180deg, rgba(227, 242, 253, 0.5) 0%, rgba(255, 255, 255, 0) 100%)',
          zIndex: 0,
        }
      }}>
        <BlurEffect color={isDark ? "#1e3a8a" : "#e3f2fd"} top="20%" right="10%" size="200px" theme={theme} />
        <BlurEffect color={isDark ? "#1e40af" : "#bbdefb"} bottom="30%" left="15%" size="180px" theme={theme} />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto', mb: 6 }}>
          <Typography
            variant="overline"
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 2,
              display: 'block'
            }}
          >
            ARCHITECTURE
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 800,
              mb: 4,
              letterSpacing: '-0.5px',
              color: isDark ? 'primary.light' : undefined,
              background: isDark
                ? undefined
                : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
              backgroundClip: isDark ? undefined : 'text',
              textFillColor: isDark ? undefined : 'transparent',
              WebkitBackgroundClip: isDark ? undefined : 'text',
              WebkitTextFillColor: isDark ? undefined : 'transparent',
            }}
          >
            System Architecture
          </Typography>
          <Typography
            variant="body1"
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'text.secondary'
            }}
          >
            Our platform connects your entire water treatment infrastructure through a secure, scalable architecture
          </Typography>
        </Box>
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: '24px',
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: '1000px',
            mx: 'auto',
            background: isDark
              ? 'linear-gradient(145deg, rgba(17,24,39,0.9) 0%, rgba(31,41,55,0.7) 100%)'
              : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: isDark 
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}>
            {nodes.map((node, index) => (
              <React.Fragment key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  flex: 1,
                  minWidth: isMobile ? '100%' : 'auto',
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${node.color} 0%, ${alpha(node.color, 0.7)} 100%)`,
                    color: 'white',
                    boxShadow: `0 8px 16px ${alpha(node.color, 0.3)}`,
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${alpha(node.color, 0.4)}`,
                    }
                  }}>
                    {node.icon}
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      textAlign: 'center',
                      color: node.color
                    }}
                  >
                    {node.label}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textAlign: 'center',
                      color: 'text.secondary',
                      fontSize: '0.85rem',
                      maxWidth: 200
                    }}
                  >
                    {node.description}
                  </Typography>
                </Box>
                
                {index < nodes.length - 1 && (
                  <Box sx={{ 
                    display: isMobile ? 'none' : 'flex',
                    alignItems: 'center',
                    color: 'primary.main',
                    opacity: 0.7
                  }}>
                    <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
                  </Box>
                )}
                
                {index < nodes.length - 1 && isMobile && (
                  <Box sx={{ 
                    width: '100%', 
                    height: 40, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'primary.main',
                    opacity: 0.7
                  }}>
                    <ArrowForwardIosIcon sx={{ fontSize: 20, transform: 'rotate(90deg)' }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);
  const [activeSection, setActiveSection] = useState('hero');
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const [openArchitectureDialog, setOpenArchitectureDialog] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  const isDark = theme.palette.mode === 'dark';
  
  // Refs for sections
  const heroRef = useRef(null);
  const purposeRef = useRef(null);
  const architectureRef = useRef(null);
  const featuresRef = useRef(null);
  const benefitsRef = useRef(null);
  const ctaRef = useRef(null);
  const featuresContainerRef = useRef(null);

  // Use Intersection Observer for more reliable section detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setActiveSection(sectionId);
          
          // Reset visible features when not in features section
          if (sectionId !== 'features') {
            setVisibleFeatures([]);
          }
        }
      });
    }, options);

    // Observe all section elements
    if (heroRef.current) observer.observe(heroRef.current);
    if (purposeRef.current) observer.observe(purposeRef.current);
    if (architectureRef.current) observer.observe(architectureRef.current);
    if (featuresRef.current) observer.observe(featuresRef.current);
    if (benefitsRef.current) observer.observe(benefitsRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Separate effect for feature animations
  useEffect(() => {
    if (activeSection !== 'features') return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Get the index of the feature card
          const index = parseInt(entry.target.dataset.index);
          if (!isNaN(index)) {
            setVisibleFeatures(prev => {
              if (prev.includes(index)) return prev;
              return [...prev, index].sort((a, b) => a - b);
            });
          }
        }
      });
    }, options);

    // Observe all feature cards
    const featureCards = document.querySelectorAll('.feature-card');
    featureCards.forEach(card => observer.observe(card));

    return () => {
      observer.disconnect();
    };
  }, [activeSection]);

  const handleOpenArchitectureDialog = () => {
    setOpenArchitectureDialog(true);
  };

  const handleCloseArchitectureDialog = () => {
    setOpenArchitectureDialog(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const features = [
    {
      icon: <WaterDropIcon sx={{ fontSize: 28 }} />,
      title: 'Real-Time Plant Monitoring',
      description: 'Live sensor data from critical points including inflow/outflow, chemical dosing, pH levels, turbidity, dissolved oxygen, and more'
    },
    {
      icon: <SensorsIcon sx={{ fontSize: 28 }} />,
      title: 'IoT Integration',
      description: 'Seamless connection to IoT gateways, PLCs, and industrial sensors for real-time data collection and remote control'
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 28 }} />,
      title: 'Smart Alerts',
      description: 'Configurable threshold-based alerts for abnormal conditions with multi-channel notifications via email, SMS, and in-app'
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 28 }} />,
      title: 'Advanced Analytics',
      description: 'Comprehensive data analysis and reporting for performance optimization and regulatory compliance'
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
      title: 'Project Assignment & Tracking',
      description: 'Assign and track maintenance tasks, monitor progress, and manage deadlines across multiple facilities'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 28 }} />,
      title: 'User & Role Management',
      description: 'Secure login with role-based access control for internal staff and external stakeholders'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 28 }} />,
      title: 'Activity Logging & Audit Trails',
      description: 'Automated logging of user actions, system events, and operational anomalies for compliance and troubleshooting'
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 28 }} />,
      title: 'Remote Access & Control',
      description: 'Monitor and manage your entire water infrastructure from anywhere with secure remote access'
    }
  ];

  const benefits = [
    { icon: <SpeedIcon />, text: 'Enhanced Operational Efficiency' },
    { icon: <CheckCircleIcon />, text: 'Proactive Maintenance & Reduced Downtime' },
    { icon: <VerifiedUserIcon />, text: 'Regulatory Compliance Assurance' },
    { icon: <SavingsIcon />, text: 'Cost Reduction & Resource Optimization' },
    { icon: <DashboardIcon />, text: 'Scalable Multi-Site Management' },
    { icon: <SecurityIcon />, text: 'Remote Access & Control' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', position: 'relative' }}>
      {/* Dark Mode Toggle Button */}
      <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
        <IconButton
          onClick={colorMode.toggleColorMode}
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 1000,
            bgcolor: isDark ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)',
            color: isDark ? 'primary.light' : 'primary.main',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            backdropFilter: 'blur(10px)',
            boxShadow: isDark 
              ? '0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              bgcolor: isDark ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.95)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
          }}
          aria-label="Toggle dark mode"
        >
          {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Tooltip>
      
      <GlobalBackground theme={theme} />
      
      {/* Hero Section */}
      <Box
        id="hero"
        ref={heroRef}
        sx={{
          position: 'relative',
          color: 'white',
          py: { xs: 10, md: 16 },
          overflow: 'hidden',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '250px',
            background: isDark
              ? 'linear-gradient(to bottom, transparent 0%, rgba(15, 20, 36, 0.2) 20%, rgba(15, 20, 36, 0.5) 50%, rgba(15, 20, 36, 0.85) 80%, rgba(15, 20, 36, 1) 100%)'
              : 'linear-gradient(to bottom, transparent 0%, rgba(245, 249, 255, 0.2) 20%, rgba(245, 249, 255, 0.5) 50%, rgba(245, 249, 255, 0.85) 80%, rgba(245, 249, 255, 1) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          }
        }}
      >
        {/* Use the same hero gradient colors in both light and dark mode */}
        <GradientBackground colors={['#1976d2', '#0d47a1']} theme={theme} />
        {/* Water-themed blur effects for dark mode, original colors for light mode */}
        <BlurEffect 
          color={isDark ? "#14b8a6" : "#64b5f6"} 
          top="10%" 
          left="5%" 
          size="300px" 
          theme={theme} 
        />
        <BlurEffect 
          color={isDark ? "#06b6d4" : "#42a5f5"} 
          bottom="10%" 
          right="5%" 
          size="250px" 
          theme={theme} 
        />
        <BlurEffect 
          color={isDark ? "#00a3ff" : "#90caf9"} 
          top="40%" 
          right="20%" 
          size="200px" 
          theme={theme} 
        />
        {/* Additional water color blur effects for dark mode only */}
        {isDark && (
          <>
            <BlurEffect color="#38bdf8" top="60%" left="15%" size="220px" theme={theme} />
            <BlurEffect color="#0891b2" bottom="20%" left="30%" size="180px" theme={theme} />
            <BlurEffect color="#0d9488" top="25%" right="35%" size="200px" theme={theme} />
          </>
        )}
        <GrainTexture opacity={isDark ? 0.08 : 0.05} />
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                opacity: activeSection === 'hero' ? 1 : 0,
                transform: activeSection === 'hero' ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease'
              }}>
                <Box 
                  component="img" 
                  src={wteLogo} 
                  alt="WTE OMS Logo" 
                  sx={{ 
                    height: '80px', 
                    mb: 4,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                  }} 
                />
                <Typography
                  variant={isMobile ? 'h4' : 'h2'}
                  component="h1"
                  gutterBottom
                  sx={{ 
                    fontWeight: 800,
                    letterSpacing: '-1px',
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: 0,
                      width: '80px',
                      height: '4px',
                      background: 'linear-gradient(90deg, #64b5f6 0%, #42a5f5 100%)',
                      borderRadius: '2px',
                    }
                  }}
                >
                  REMOTE DATA MONITORING SYSTEM (RDMS)
                </Typography>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{ 
                    mb: 4, 
                    opacity: 0.9,
                    lineHeight: 1.4,
                    fontWeight: 400
                  }}
                >
                  The intelligent platform that transforms water treatment operations through automation, real-time monitoring, and data-driven insights
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                  <ModernButton
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      }
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Get Started
                  </ModernButton>
                  <ModernButton
                    variant="outlined"
                    endIcon={<ArrowRightAltIcon />}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      '&:hover': {
                        borderColor: 'grey.300',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                    onClick={() => navigate('/signup')}
                  >
                    Request Demo
                  </ModernButton>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box 
                sx={{ 
                  opacity: activeSection === 'hero' ? 1 : 0,
                  transform: activeSection === 'hero' ? 'scale(1)' : 'scale(0.95)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                  transitionDelay: '0.2s'
                }}
              >
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.3) 0%, rgba(13, 71, 161, 0.3) 100%)',
                      zIndex: 1
                    }
                  }}
                >
                  <Box 
                    component="img"
                    src={dashboardScreenshot}
                    alt="WTE OMS Dashboard"
                    sx={{
                      width: '100%',
                      display: 'block',
                      opacity: 0.9
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Purpose Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: isDark ? 'rgba(15, 20, 36, 0.95)' : 'rgba(245, 249, 255, 0.95)',
        }}
      >
        <Container 
          id="purpose" 
          ref={purposeRef}
          maxWidth="lg" 
          sx={{ 
            py: { xs: 10, md: 14 }, 
            position: 'relative', 
            zIndex: 2,
            border: 'none',
            outline: 'none'
          }}
        >
        <BlurEffect color={isDark ? "#1e3a8a" : "#e3f2fd"} top="20%" right="10%" size="200px" theme={theme} />
        <BlurEffect color={isDark ? "#1e40af" : "#bbdefb"} bottom="30%" left="15%" size="180px" theme={theme} />
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto', mb: 8 }}>
          <Box sx={{ 
            opacity: activeSection === 'purpose' ? 1 : 0,
            transform: activeSection === 'purpose' ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
            <Typography
              variant="overline"
              sx={{ 
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 2,
                display: 'block'
              }}
            >
              OUR MISSION
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 800,
                mb: 4,
                letterSpacing: '-0.5px',
                color: isDark ? 'primary.light' : undefined,
                background: isDark
                  ? undefined
                  : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
                backgroundClip: isDark ? undefined : 'text',
                textFillColor: isDark ? undefined : 'transparent',
                WebkitBackgroundClip: isDark ? undefined : 'text',
                WebkitTextFillColor: isDark ? undefined : 'transparent',
              }}
            >
              Transforming Water Treatment
            </Typography>
            <Typography
              variant="body1"
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'text.secondary',
                mb: 6
              }}
            >
              WTE RDMS is designed to address the complex challenges faced by water treatment facilities. 
              Our platform modernizes operations through automated monitoring, ensuring safe and efficient 
              processes while enabling remote management from a centralized interface. With WTE RDMS, 
              you can reduce operational costs, improve compliance, and make data-driven decisions.
            </Typography>
          </Box>
        </Box>
        </Container>
      </Box>

      {/* Architecture Section */}
      <Box 
        id="architecture"
        ref={architectureRef}
        sx={{ 
          py: { xs: 10, md: 14 },
          position: 'relative',
          bgcolor: isDark ? 'rgba(15, 20, 36, 0.95)' : 'rgba(245, 249, 255, 0.95)',
          // Top overlay to smoothly continue the hero's bottom gradient into this section
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '250px',
            background: isDark
              ? 'linear-gradient(to top, transparent 0%, rgba(15, 20, 36, 0.2) 20%, rgba(15, 20, 36, 0.5) 50%, rgba(15, 20, 36, 0.85) 80%, rgba(15, 20, 36, 1) 100%)'
              : 'linear-gradient(to top, transparent 0%, rgba(245, 249, 255, 0.2) 20%, rgba(245, 249, 255, 0.5) 50%, rgba(245, 249, 255, 0.85) 80%, rgba(245, 249, 255, 1) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ 
          opacity: activeSection === 'architecture' ? 1 : 0,
          transform: activeSection === 'architecture' ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
          position: 'relative',
          zIndex: 2
        }}>
          <ArchitectureFlow theme={theme} />
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <ModernButton
              variant="contained"
              startIcon={<ArchitectureIcon />}
              onClick={handleOpenArchitectureDialog}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              View Architecture Diagram
            </ModernButton>
          </Box>
        </Box>
      </Box>

      {/* Architecture Diagram Dialog */}
      <Dialog
        open={openArchitectureDialog}
        onClose={handleCloseArchitectureDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
            onClick={handleCloseArchitectureDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: isDark ? 'text.secondary' : 'grey.500',
              bgcolor: isDark ? 'background.paper' : 'white',
              border: isDark ? '1px solid' : 'none',
              borderColor: isDark ? 'divider' : 'none',
              '&:hover': {
                bgcolor: isDark ? 'action.hover' : 'grey.100',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '80vh',
            position: 'relative',
            overflow: 'auto'
          }}>
            <Box
              component="img"
              src={architectureDiagram}
              alt="System Architecture Diagram"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          </Box>
          <Box sx={{ 
            position: 'absolute', 
            bottom: 16, 
            right: 16, 
            display: 'flex', 
            gap: 1,
            bgcolor: isDark ? 'rgba(17,24,39,0.9)' : 'rgba(255, 255, 255, 0.9)',
            p: 1,
            borderRadius: '8px',
            border: isDark ? '1px solid' : 'none',
            borderColor: isDark ? 'divider' : 'none',
            boxShadow: isDark 
              ? '0 2px 8px rgba(0,0,0,0.3)'
              : '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <IconButton 
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.5}
              sx={{ 
                bgcolor: isDark ? 'transparent' : 'white',
                color: isDark ? 'text.primary' : undefined,
                '&:hover': { bgcolor: isDark ? 'action.hover' : 'grey.100' },
                '&.Mui-disabled': { opacity: 0.5 }
              }}
            >
              <RemoveIcon />
            </IconButton>
            <IconButton 
              onClick={handleResetZoom}
              sx={{ 
                bgcolor: isDark ? 'transparent' : 'white',
                color: isDark ? 'text.primary' : undefined,
                '&:hover': { bgcolor: isDark ? 'action.hover' : 'grey.100' }
              }}
            >
              <RestartAltIcon />
            </IconButton>
            <IconButton 
              onClick={handleZoomIn}
              disabled={zoomLevel >= 3}
              sx={{ 
                bgcolor: isDark ? 'transparent' : 'white',
                color: isDark ? 'text.primary' : undefined,
                '&:hover': { bgcolor: isDark ? 'action.hover' : 'grey.100' },
                '&.Mui-disabled': { opacity: 0.5 }
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Features Section */}
      <Box 
        id="features"
        ref={featuresRef}
        sx={{ 
          bgcolor: isDark ? 'rgba(15, 20, 36, 0.95)' : 'rgba(250, 250, 250, 0.95)', 
          py: { xs: 10, md: 14 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: isDark
              ? 'linear-gradient(to bottom, rgba(15, 20, 36, 1) 0%, rgba(15, 20, 36, 0.8) 20%, rgba(15, 20, 36, 0.5) 50%, rgba(15, 20, 36, 0.2) 80%, transparent 100%)'
              : 'linear-gradient(to bottom, rgba(250, 250, 250, 1) 0%, rgba(250, 250, 250, 0.8) 20%, rgba(250, 250, 250, 0.5) 50%, rgba(250, 250, 250, 0.2) 80%, transparent 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: isDark
              ? 'linear-gradient(to bottom, transparent 0%, rgba(15, 20, 36, 0.2) 20%, rgba(15, 20, 36, 0.5) 50%, rgba(15, 20, 36, 0.8) 80%, rgba(15, 20, 36, 1) 100%)'
              : 'linear-gradient(to bottom, transparent 0%, rgba(245, 249, 255, 0.2) 20%, rgba(245, 249, 255, 0.5) 50%, rgba(245, 249, 255, 0.8) 80%, rgba(245, 249, 255, 1) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          }
        }}
      >
        <BlurEffect color={isDark ? "#1e3a8a" : "#bbdefb"} top="10%" left="5%" size="250px" theme={theme} />
        <BlurEffect color={isDark ? "#2563eb" : "#90caf9"} bottom="10%" right="5%" size="200px" theme={theme} />
        <BlurEffect color={isDark ? "#1e40af" : "#e3f2fd"} top="30%" right="15%" size="180px" theme={theme} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto', mb: 8 }}>
            <Box sx={{ 
              opacity: activeSection === 'features' ? 1 : 0,
              transform: activeSection === 'features' ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
              <Typography
                variant="overline"
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 2,
                  display: 'block'
                }}
              >
                CAPABILITIES
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  mb: 4,
                  letterSpacing: '-0.5px',
                  color: isDark ? 'primary.light' : undefined,
                  background: isDark
                    ? undefined
                    : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
                  backgroundClip: isDark ? undefined : 'text',
                  textFillColor: isDark ? undefined : 'transparent',
                  WebkitBackgroundClip: isDark ? undefined : 'text',
                  WebkitTextFillColor: isDark ? undefined : 'transparent',
                }}
              >
                Powerful Tools for Water Treatment
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  color: 'text.secondary'
                }}
              >
                Our platform offers a complete suite of tools designed specifically for water treatment operations
              </Typography>
            </Box>
          </Box>
          <Box ref={featuresContainerRef}>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box 
                    className="feature-card"
                    data-index={index}
                    sx={{ 
                      opacity: visibleFeatures.includes(index) ? 1 : 0,
                      transform: visibleFeatures.includes(index) ? 'translateY(0)' : 'translateY(50px)',
                      transition: 'opacity 0.5s ease, transform 0.5s ease',
                      transitionDelay: `${index * 100}ms`
                    }}
                  >
                    <FeatureCard {...feature} theme={theme} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: isDark ? 'rgba(15, 20, 36, 0.95)' : 'rgba(245, 249, 255, 0.95)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: isDark
              ? 'linear-gradient(to bottom, rgba(15, 20, 36, 1) 0%, rgba(15, 20, 36, 0.8) 20%, rgba(15, 20, 36, 0.5) 50%, rgba(15, 20, 36, 0.2) 80%, rgba(15, 20, 36, 0) 100%)'
              : 'linear-gradient(to bottom, rgba(245, 249, 255, 1) 0%, rgba(245, 249, 255, 0.8) 20%, rgba(245, 249, 255, 0.5) 50%, rgba(245, 249, 255, 0.2) 80%, rgba(245, 249, 255, 0) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          },
        }}
      >
        <BlurEffect color={isDark ? "#1e3a8a" : "#e8f5e9"} top="20%" left="10%" size="200px" theme={theme} />
        <BlurEffect color={isDark ? "#1e40af" : "#c8e6c9"} bottom="30%" right="15%" size="180px" theme={theme} />
        <Container 
          id="benefits" 
          ref={benefitsRef}
          maxWidth="lg" 
          sx={{ 
            py: { xs: 10, md: 14 }, 
            pb: { xs: 4, md: 6 },
            position: 'relative', 
            zIndex: 2,
            border: 'none',
            outline: 'none'
          }}
        >
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto', mb: 8 }}>
          <Box sx={{ 
            opacity: activeSection === 'benefits' ? 1 : 0,
            transform: activeSection === 'benefits' ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
            <Typography
              variant="overline"
              sx={{ 
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 2,
                display: 'block'
              }}
            >
              VALUE PROPOSITION
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 800,
                mb: 4,
                letterSpacing: '-0.5px',
                color: isDark ? 'primary.light' : undefined,
                background: isDark
                  ? undefined
                  : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
                backgroundClip: isDark ? undefined : 'text',
                textFillColor: isDark ? undefined : 'transparent',
                WebkitBackgroundClip: isDark ? undefined : 'text',
                WebkitTextFillColor: isDark ? undefined : 'transparent',
              }}
            >
              Measurable Results
            </Typography>
            <Typography
              variant="body1"
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              WTE RDMS delivers measurable results that impact your bottom line and operational excellence
            </Typography>
          </Box>
        </Box>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: '24px',
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: '800px',
            mx: 'auto',
            background: isDark
              ? 'linear-gradient(145deg, rgba(17,24,39,0.9) 0%, rgba(31,41,55,0.7) 100%)'
              : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: isDark 
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box sx={{ 
            opacity: activeSection === 'benefits' ? 1 : 0,
            transform: activeSection === 'benefits' ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
            <List>
              {benefits.map((benefit, index) => (
                <React.Fragment key={index}>
                  <BenefitItem {...benefit} theme={theme} />
                  {index < benefits.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Paper>
        </Container>

        <Box sx={{ position: 'relative', zIndex: 2, pb: { xs: 4, md: 6 } }}>
          <ClientsShowcase />
        </Box>

        {/* Soft handoff into CTA — placed below clients so it can't cut across the heading */}
        <Box
          aria-hidden
          sx={{
            height: { xs: 100, md: 140 },
            background: isDark
              ? 'linear-gradient(to bottom, rgba(13, 71, 161, 0) 0%, rgba(13, 71, 161, 0.45) 45%, rgba(13, 71, 161, 1) 100%)'
              : 'linear-gradient(to bottom, rgba(25, 118, 210, 0) 0%, rgba(25, 118, 210, 0.45) 45%, rgba(25, 118, 210, 1) 100%)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* CTA Section */}
      <Box 
        id="cta"
        ref={ctaRef}
        sx={{ 
          position: 'relative',
          color: 'white',
          py: { xs: 10, md: 14 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '250px',
            background: isDark
              ? 'linear-gradient(to bottom, rgba(13, 71, 161, 1) 0%, rgba(13, 71, 161, 0.9) 20%, rgba(13, 71, 161, 0.7) 50%, rgba(13, 71, 161, 0.4) 80%, rgba(13, 71, 161, 0) 100%)'
              : 'linear-gradient(to bottom, rgba(25, 118, 210, 1) 0%, rgba(25, 118, 210, 0.9) 20%, rgba(25, 118, 210, 0.7) 50%, rgba(25, 118, 210, 0.4) 80%, rgba(25, 118, 210, 0) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          }
        }}
      >
        <GradientBackground theme={theme} />
        <BlurEffect color={isDark ? "#1e3a8a" : "#64b5f6"} top="10%" left="5%" size="300px" theme={theme} />
        <BlurEffect color={isDark ? "#1e40af" : "#42a5f5"} bottom="10%" right="5%" size="250px" theme={theme} />
        <BlurEffect color={isDark ? "#2563eb" : "#90caf9"} top="40%" right="20%" size="200px" theme={theme} />
        <GrainTexture opacity={isDark ? 0.08 : 0.05} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            opacity: activeSection === 'cta' ? 1 : 0,
            transform: activeSection === 'cta' ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
            <Box sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  mb: 4,
                  letterSpacing: '-0.5px'
                }}
              >
                Ready to Transform Your Water Treatment Operations?
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 6,
                  opacity: 0.9
                }}
              >
                Join leading water treatment facilities in modernizing their operations with WTE RDMS
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mt: 4 }}
              >
                <ModernButton
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    }
                  }}
                  onClick={() => navigate('/signup')}
                >
                  Sign Up Now
                </ModernButton>
                <ModernButton
                  variant="outlined"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'grey.300',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                  onClick={() => navigate('/login')}
                >
                  Login
                </ModernButton>
                <ModernButton
                  variant="text"
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                  onClick={() => window.open('https://wteinfra.com/contact', '_blank')}
                >
                  Contact Sales
                </ModernButton>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Site footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: isDark ? 'rgba(10, 14, 28, 1)' : 'rgba(13, 71, 161, 1)',
          color: 'rgba(255,255,255,0.9)',
          py: { xs: 3, md: 4 },
          borderTop: '1px solid',
          borderColor: 'rgba(255,255,255,0.12)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                WTE Infra Projects Pvt Ltd
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                © {new Date().getFullYear()} WTE. All rights reserved.
              </Typography>
            </Box>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                component={RouterLink}
                to="/privacy-policy"
                variant="body2"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  opacity: 0.9,
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline', opacity: 1 },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                component="a"
                href="https://wteinfra.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  opacity: 0.9,
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline', opacity: 1 },
                }}
              >
                Contact
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 